import {
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CustomTooltip from '../toolTip/Tooltip';
import { DrawerHeader } from 'src/styledComponents/styledBoxs';
import MenuIcon from 'src/resources/icons/menuIcon';
import VesisLight from '../../resources/icons/vesisLight';
import { content } from 'src/common/lang/en-US';

interface MenuItemType {
    id: string;
    link: string;
    name: string;
}

const menuItems: MenuItemType[] = [
    { id: 'home', link: '/', name: content.navbar.menu.home },
    { id: 'expertise', link: '/#expertise', name: content.navbar.menu.expertise },
    { id: 'products', link: '/#products', name: content.navbar.menu.products },
    { id: 'company', link: '/', name: content.navbar.menu.company },
    { id: 'support', link: '/', name: content.navbar.menu.support },
    { id: 'careers', link: '/', name: content.navbar.menu.careers },
];

const clickableIds = new Set(['home', 'expertise', 'products']);

const NavBar: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({});
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [activeMenuItem, setActiveMenuItem] = useState<string>(menuItems[0].id);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleMenuClick = (id: string): void => {
        setActiveMenuItem(id);
        const menuItem = menuItems.find((item) => item.id === id);
        if (menuItem?.link) {
            // Split the link into pathname and hash (if present)
            const [pathname, hash] = menuItem.link.split('#');

            if (window.location.pathname === pathname) {
                if (id === 'home') {
                    window.history.pushState(null, '', '/');
                    // Scroll to the top when clicking "Home"
                    window.scrollTo({ behavior: 'smooth', top: 0 });
                } else if (hash) {
                    // Scroll to section if there's a hash
                    window.history.pushState(null, '', menuItem.link);
                    const element = document.getElementById(hash);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                    }
                }
                handleTooltipClose();
                setIsDrawerOpen(false);
            } else {
                // Otherwise, navigate normally.
                handleTooltipToggle(id);
                navigate(menuItem.link);
            }
        }
    };

    const handleMobileMenuOpen = (): void => {
        setIsDrawerOpen(true);
    };

    const handleMobileMenuClose = (): void => {
        setIsDrawerOpen(false);
        handleTooltipClose();
    };

    const handleTooltipToggle = (id: string): void => {
        setTooltipOpen((prev) => ({
            ...Object.fromEntries(Object.keys(prev).map((key) => [key, false])),
            [id]: !prev[id],
        }));
    };

    const handleTooltipClose = (): void => {
        setTooltipOpen({});
    };

    useEffect(() => {
        const handleScroll = (): void => {
            setIsVisible(true); // Always show navbar when scrolling
            setIsScrolled(window.scrollY > 10);

            const isTopSection = window.scrollY < 100;

            if (scrollTimeout.current) clearTimeout(scrollTimeout.current); // Clear previous timeout

            scrollTimeout.current = setTimeout(() => {
                if (!isTopSection) {
                    setIsVisible(false); // Hide navbar only if user has scrolled past top section
                }
            }, 1500);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
        };
    }, []);

    // Scroll listener to update active menu item based on scroll position.
    useEffect(() => {
        // Find the menu item corresponding to the current path
        const matchedMenuItem = menuItems.find((item) => item.link === location.pathname);

        if (matchedMenuItem) {
            setActiveMenuItem(matchedMenuItem.id);
        } else if (location.pathname === '/') {
            setActiveMenuItem('home');
        } else {
            setActiveMenuItem('');
            return;
        }

        const handleScroll = (): void => {
            // Check if the Expertise and Products sections exist on the page
            const expertiseSection = document.getElementById('expertise');
            const productsSection = document.getElementById('products');
            const scrollY = window.scrollY;

            // If the expertise section is in view
            if (expertiseSection) {
                const rect = expertiseSection.getBoundingClientRect();
                if (rect.top <= 150 && rect.bottom >= 150) {
                    setActiveMenuItem('expertise');
                    return;
                }
            }

            // If the products section is in view
            if (productsSection) {
                const rect = productsSection.getBoundingClientRect();
                if (rect.top <= 150 && rect.bottom >= 150) {
                    setActiveMenuItem('products');
                    return;
                }
            }

            // If we're near the top, set the active menu to "home"
            if (scrollY < 100) {
                setActiveMenuItem('home');
            }
        };

        // Only add the scroll event listener if we are on the home page
        if (location.pathname === '/') {
            window.addEventListener('scroll', handleScroll);
            handleScroll(); // Call immediately in case the user refreshes mid-scroll
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [location.pathname]);

    const renderMobileMenu = (): ReactElement => (
        <Drawer
            anchor="right"
            onClose={handleMobileMenuClose}
            open={isDrawerOpen}
            sx={{
                '& .MuiDrawer-paper': {
                    backdropFilter: 'blur(5px)',
                    bgcolor: 'background.bgGray',
                    height: '100%',
                    width: '100%',
                },
            }}
            variant="temporary"
        >
            <DrawerHeader>
                <IconButton onClick={handleMobileMenuClose}>
                    {theme.direction === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
                <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'center', mr: 4, width: '100%' }}>
                    <VesisLight height="40" width="48" />
                </Box>
            </DrawerHeader>
            <Divider />
            <List sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', mt: 2, width: '100%' }}>
                {menuItems.map((item) => {
                    const menuItemContent = (
                        <Typography
                            color={item.id === activeMenuItem ? 'primary.main' : 'text.primary'}
                            variant="body2"
                        >
                            {item.name}
                        </Typography>
                    );

                    return clickableIds.has(item.id) ? (
                        <ListItem key={item.id} sx={{ width: '100%' }}>
                            <ListItemButton
                                onClick={() => handleMenuClick(item.id)}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                }}
                            >
                                {menuItemContent}
                            </ListItemButton>
                        </ListItem>
                    ) : (
                        <ListItem key={item.id} sx={{ width: '100%' }}>
                            <ListItemButton
                                onClick={() => handleTooltipToggle(item.id)}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    width: '100%',
                                }}
                            >
                                <CustomTooltip
                                    key={item.id}
                                    open={tooltipOpen[item.id] || false}
                                    placement="left"
                                    title={content.common.text.comingSoon}
                                >
                                    {menuItemContent}
                                </CustomTooltip>
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Drawer>
    );

    const renderDesktopMenu = (): ReactElement => (
        <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            sx={{ padding: '6px 10px', width: '80%' }}
        >
            {menuItems.map((item) => {
                const menuButtonContent = <Typography variant="caption1">{item.name}</Typography>;

                return clickableIds.has(item.id) ? (
                    <Button
                        key={item.id}
                        onClick={() => handleMenuClick(item.id)}
                        sx={{ color: item.id === activeMenuItem ? 'primary.main' : 'inherit' }}
                    >
                        {menuButtonContent}
                    </Button>
                ) : (
                    <CustomTooltip key={item.id} title={content.common.text.comingSoon}>
                        <Button color={item.id === activeMenuItem ? 'primary' : 'inherit'}>{menuButtonContent}</Button>
                    </CustomTooltip>
                );
            })}
        </Box>
    );

    return (
        <Box
            alignItems="center"
            bgcolor="background.bgGray"
            display="flex"
            justifyContent="space-between"
            sx={{
                backdropFilter: 'blur(2px)',
                padding: { lg: '14px 42px', md: '10px 38px', sm: '10px 30px', xs: '10px 20px', xxl: '40px 42px' },
                position: 'fixed',
                // top: isVisible ? (isScrolled ? '0px' : '42px') : '-100px', // Pulls navbar up when inactive
                top: {
                    lg: isVisible ? (isScrolled ? '0px' : '42px') : '-100px',
                    md: isVisible ? (isScrolled ? '0px' : '42px') : '-100px',
                    sm: isVisible ? (isScrolled ? '0px' : '42px') : '-100px',
                    xl: isVisible ? (isScrolled ? '0px' : '42px') : '-100px',
                    xs: isVisible ? (isScrolled ? '0px' : '42px') : '-100px', // 42px for all other sizes
                    xxl: isVisible ? (isScrolled ? '0px' : '80px') : '-100px', // 80px for xxl screens
                },
                transition: 'top 0.3s ease-in-out', // Smooth transition
                width: '100%',
                zIndex: 1100,
            }}
        >
            <IconButton aria-label="home" onClick={() => handleMenuClick(menuItems[0].id)}>
                <VesisLight height={isMobile ? '40' : '50'} width={isMobile ? '48' : '58'} />
            </IconButton>
            {isMobile ? (
                <Box display="flex" gap={2}>
                    <IconButton aria-label="menu" id="mobile-menu-button" onClick={handleMobileMenuOpen}>
                        <MenuIcon />
                    </IconButton>
                    {renderMobileMenu()}
                </Box>
            ) : (
                renderDesktopMenu()
            )}
        </Box>
    );
};

export default NavBar;
